/**
 * API Service for JobsReadyAI Backend
 * Handles all API calls to the backend Lambda functions
 */

import * as Sentry from "@sentry/react";
import { trackApiCall } from '../utils/cloudwatchRum';

const { logger } = Sentry;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Type compatible with Auth0's getAccessTokenSilently
// Auth0's function has overloads, so we accept a function that can be called
// with our expected signature (which is compatible with Auth0's first overload)
export type GetAccessTokenSilently = (
  options?: {
    audience?: string;
    scope?: string;
    cacheMode?: string;
  }
) => Promise<string>;

interface ApiRequestOptions extends RequestInit {
  headers?: HeadersInit;
}

/**
 * Custom error class for authentication errors that require user interaction
 */
export class AuthenticationRequiredError extends Error {
  constructor(message: string = 'Authentication required') {
    super(message);
    this.name = 'AuthenticationRequiredError';
  }
}

/**
 * Custom error class for insufficient credits
 */
export class InsufficientCreditsError extends Error {
  constructor(message: string = 'Insufficient credits') {
    super(message);
    this.name = 'InsufficientCreditsError';
  }
}

/**
 * Get the access token from Auth0
 * @param getAccessTokenSilently - Auth0 function to get token
 * @param forceRefresh - If true, bypasses cache to get a fresh token
 */
const getAuthToken = async (getAccessTokenSilently: GetAccessTokenSilently, forceRefresh: boolean = false): Promise<string> => {
  try {
    const options: {
      audience?: string;
      scope?: string;
      cacheMode?: string;
    } = {
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      scope: 'read:current_user'
    };
    
    // Force refresh by bypassing cache if needed
    if (forceRefresh) {
      options.cacheMode = 'off';
    }
    
    return await getAccessTokenSilently(options);
  } catch (error: any) {
    logger.error('Error getting access token', {
      error: error?.message || String(error),
      errorCode: error?.error,
    });
    
    // Capture authentication errors in Sentry (except for expected auth flow errors)
    const errorMessage = error?.error || error?.message || String(error);
    const errorCode = error?.error || '';
    
    if (errorCode === 'consent_required' || 
        errorCode === 'login_required' ||
        errorMessage.toLowerCase().includes('consent_required') ||
        errorMessage.toLowerCase().includes('login_required') ||
        errorMessage.toLowerCase().includes('consent required') ||
        errorMessage.toLowerCase().includes('login required')) {
      // Don't capture expected auth flow errors
      throw new AuthenticationRequiredError('Your session has expired. Please log in again.');
    }
    
    // Capture unexpected authentication errors
    Sentry.captureException(error, {
      tags: { component: 'api', function: 'getAuthToken' },
    });
    
    throw error;
  }
};

/**
 * Make an authenticated API request
 * Automatically retries with a fresh token if a 401 error occurs
 */
const apiRequest = async <T = any>(
  endpoint: string,
  options: ApiRequestOptions = {},
  getAccessTokenSilently: GetAccessTokenSilently,
  retryCount: number = 0
): Promise<T> => {
  const startTime = Date.now();
  const apiName = `${options.method || 'GET'} ${endpoint}`;
  
  return Sentry.startSpan(
    {
      op: "http.client",
      name: apiName,
    },
    async () => {
      const MAX_RETRIES = 1; // Only retry once with fresh token
      let success = false;
      
      try {
        // Get token (force refresh on retry)
        const token = await getAuthToken(getAccessTokenSilently, retryCount > 0);
        
        const fullUrl = `${API_BASE_URL}${endpoint}`;
        
        const response = await fetch(fullUrl, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers
          }
        });

      // If we get a 401 and haven't retried yet, try again with a fresh token
      if (response.status === 401 && retryCount < MAX_RETRIES) {
        return apiRequest<T>(endpoint, options, getAccessTokenSilently, retryCount + 1);
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorData = error as { error?: string; message?: string; code?: string };
        
        // Handle insufficient credits error (402 Payment Required)
        if (response.status === 402 || errorData.code === 'INSUFFICIENT_CREDITS') {
          throw new InsufficientCreditsError(errorData.message || 'You have no credits remaining. Please purchase more credits.');
        }
        
        const apiError = new Error(errorData.error || errorData.message || `API request failed: ${response.statusText}`);
        
        // Don't log client errors (400-499) as internal errors - these are expected user-facing errors
        // Only log server errors (500+) and unexpected errors
        if (response.status >= 500) {
          Sentry.captureException(apiError, {
            tags: { 
              component: 'api', 
              function: 'apiRequest',
              endpoint,
              statusCode: response.status,
            },
            extra: {
              endpoint,
              method: options.method || 'GET',
              statusCode: response.status,
              errorData,
            },
          });
        }
        
        throw apiError;
      }

        const result = await response.json() as T;
        success = true;
        return result;
      } catch (error) {
        // Re-throw error after tracking
        throw error;
      } finally {
        // Track API call duration in CloudWatch RUM
        const duration = Date.now() - startTime;
        trackApiCall(apiName, duration, success);
      }
    }
  );
};

/**
 * Get current user's data
 */
export const getUserData = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me', { method: 'GET' }, getAccessTokenSilently);
};

/**
 * Update user profile information
 */
export const updateUser = async (userData: any, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me', {
    method: 'PUT',
    body: JSON.stringify(userData)
  }, getAccessTokenSilently);
};

/**
 * Get user's share code and share URL
 */
export const getShareCode = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<{
  shareCode: string;
  shareUrl: string;
  referralCreditsEarned: number;
  maxReferralCredits: number;
}> => {
  return apiRequest('/users/me/share-code', {
    method: 'GET'
  }, getAccessTokenSilently);
};

/**
 * Get list of users referred by current user
 */
export const getReferrals = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<{
  referrals: Array<{
    userId: string;
    name: string;
    email: string | null;
    createdAt: string;
    hasImportedJob: boolean;
  }>;
  totalReferrals: number;
  creditsEarned: number;
  maxCredits: number;
}> => {
  return apiRequest('/users/me/referrals', {
    method: 'GET'
  }, getAccessTokenSilently);
};

/**
 * Update user credits (add or subtract)
 * @param amount - Positive to add, negative to subtract
 */
export const updateCredits = async (amount: number, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me/credits', {
    method: 'POST',
    body: JSON.stringify({ amount })
  }, getAccessTokenSilently);
};

/**
 * Initialize user on first login
 * This should be called after successful authentication
 * The backend now automatically creates users if they don't exist, so this just calls getUserData
 * @deprecated - Use getUserData directly instead. Kept for backward compatibility.
 */
export const initializeUser = async (auth0User: any, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  // Backend now automatically creates users, so just get user data
  return getUserData(getAccessTokenSilently);
};

/**
 * Get user profile (about me, experiences, education)
 * Returns null if profile doesn't exist (404)
 */
export const getProfile = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any | null> => {
  try {
    return await apiRequest('/users/me/profile', { method: 'GET' }, getAccessTokenSilently);
  } catch (error) {
    // Return null if profile doesn't exist (404), re-throw other errors
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      return null;
    }
    throw error;
  }
};

/**
 * Save user profile (about me, experiences, education)
 */
export const saveProfile = async (profileData: any, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData)
  }, getAccessTokenSilently);
};

/**
 * Get user's interview prep plan view history
 * Returns an object with a 'views' property containing date -> count mapping
 * Expected format: { views: { "2024-01-15": 3, "2024-01-16": 1, ... } }
 * Returns null if no data exists (404)
 */
export const getViewHistory = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any | null> => {
  try {
    return await apiRequest('/users/me/view-history', { method: 'GET' }, getAccessTokenSilently);
  } catch (error) {
    // Return null if view history doesn't exist (404), re-throw other errors
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      return null;
    }
    throw error;
  }
};

/**
 * Parse a job posting URL to extract job information
 * @param url - Job posting URL to parse
 */
export const parseJobUrl = async (url: string, getAccessTokenSilently: GetAccessTokenSilently): Promise<{
  title?: string;
  company?: string;
  companyWebsite?: string;
  location?: string;
  description?: string;
  url: string;
}> => {
  return apiRequest('/users/me/jobs/parse-url', {
    method: 'POST',
    body: JSON.stringify({ url })
  }, getAccessTokenSilently);
};

/**
 * Save a job posting to /users/me/jobs endpoint
 * @param jobData - Job posting data (title, company, location, description, url)
 */
export const saveJob = async (jobData: any, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me/jobs', {
    method: 'POST',
    body: JSON.stringify(jobData)
  }, getAccessTokenSilently);
};

/**
 * Get all jobs for the current user from /users/me/jobs endpoint
 */
export const getJobs = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any[]> => {
  try {
    const result = await apiRequest<any[]>('/users/me/jobs', { method: 'GET' }, getAccessTokenSilently);
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      return [];
    }
    throw error;
  }
};

/**
 * Get a single job by ID from /users/me/jobs/{jobId} endpoint
 */
export const getJob = async (jobId: string, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  try {
    return await apiRequest<any>(`/users/me/jobs/${jobId}`, { method: 'GET' }, getAccessTokenSilently);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      return null;
    }
    throw error;
  }
};

/**
 * Update job state using /users/me/jobs/{jobId} endpoint
 * @param jobId - Job posting ID
 * @param newState - New state (imported, analyzing, resume_created, applied, no_offer, offer_accepted, offer_rejected)
 */
export const updateJobState = async (jobId: string, newState: string, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest(`/users/me/jobs/${jobId}`, {
    method: 'PUT',
    body: JSON.stringify({ state: newState })
  }, getAccessTokenSilently);
};

/**
 * Get job activities from /users/me/jobs/activities endpoint
 */
export const getJobActivities = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any[]> => {
  try {
    return await apiRequest<any[]>('/users/me/jobs/activities', { method: 'GET' }, getAccessTokenSilently);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      return [];
    }
    throw error;
  }
};

/**
 * Delete a job using /users/me/jobs/{jobId} endpoint
 * @param jobId - Job posting ID
 */
export const deleteJob = async (jobId: string, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest(`/users/me/jobs/${jobId}`, {
    method: 'DELETE'
  }, getAccessTokenSilently);
};

/**
 * Re-analyze a job by resetting it to imported state and triggering enrichment again
 * @param jobId - Job posting ID
 */
export const reAnalyzeJob = async (jobId: string, getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest(`/users/me/jobs/${jobId}/re-analyze`, {
    method: 'POST'
  }, getAccessTokenSilently);
};

/**
 * Mark account for deletion (sets deleted state and delete_date 30 days in future)
 */
export const deleteAccount = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me/delete', {
    method: 'POST'
  }, getAccessTokenSilently);
};

/**
 * Undo account deletion (removes deleted state and delete_date)
 */
export const undoDeleteAccount = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me/undo-delete', {
    method: 'POST'
  }, getAccessTokenSilently);
};

/**
 * Parse resume using backend Lambda (which queues the job and processes asynchronously)
 * @param file - Resume file to parse
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Job information with jobId and status
 */
export const parseResume = async (file: File, getAccessTokenSilently: GetAccessTokenSilently): Promise<{ jobId: string; status: string; message: string }> => {
  const token = await getAuthToken(getAccessTokenSilently);
  
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/users/me/parse-resume`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type - let browser set it with boundary for multipart/form-data
    },
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    const apiError = new Error((error as { error?: string; message?: string }).error || (error as { error?: string; message?: string }).message || `API request failed: ${response.statusText}`);
    
    // Capture resume parsing errors in Sentry
    Sentry.captureException(apiError, {
      tags: { 
        component: 'api', 
        function: 'parseResume',
        statusCode: response.status,
      },
      extra: {
        statusCode: response.status,
        errorData: error,
      },
    });
    
    throw apiError;
  }

  return response.json();
};

/**
 * Get resume parsing status
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Status object with status, jobId, error, and profileData (if completed)
 */
export const getParseResumeStatus = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<{
  status: string;
  jobId: string | null;
  error: string | null;
  statusUpdatedAt: string | null;
  profileData: any | null;
}> => {
  return apiRequest('/users/me/parse-resume/status', { method: 'GET' }, getAccessTokenSilently);
};

/**
 * Generate an optimized resume based on user profile and job description
 * Now returns a queued status response (202) instead of the actual resume
 * @param jobId - The job ID to retrieve the job description from S3
 * @param templateId - Optional template ID (defaults to 'default')
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Status object with message, status, and jobId, or completed status with blob
 */
export const generateResume = async (
  jobId: string,
  templateId: string | undefined,
  getAccessTokenSilently: GetAccessTokenSilently
): Promise<{ message: string; status: string; jobId: string; blob?: Blob }> => {
  const token = await getAuthToken(getAccessTokenSilently);
  
  const response = await fetch(`${API_BASE_URL}/users/me/generate-resume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      jobId,
      templateId: templateId || 'default',
      forceRegenerate: true // Force regeneration for testing async flow
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    const apiError = new Error((error as { error?: string; message?: string }).error || (error as { error?: string; message?: string }).message || `API request failed: ${response.statusText}`);
    
    // Capture resume generation errors in Sentry
    Sentry.captureException(apiError, {
      tags: { 
        component: 'api', 
        function: 'generateResume',
        statusCode: response.status,
      },
      extra: {
        jobId,
        templateId: templateId || 'default',
        statusCode: response.status,
        errorData: error,
      },
    });
    
    throw apiError;
  }

  // Check if response is DOCX (cached resume) or JSON (queued status)
  const contentType = response.headers.get('content-type') || '';
  
  if (contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') || 
      contentType.includes('application/octet-stream')) {
    // Cached resume returned immediately
    const arrayBuffer = await response.arrayBuffer();
    const view = new Uint8Array(arrayBuffer);
    
    if (view.length > 1 && view[0] === 0x50 && view[1] === 0x4B) {
      // It's already binary, return as Blob
      const blob = new Blob([arrayBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      // Return a special object indicating immediate completion
      return { message: 'Resume generated (cached)', status: 'completed', jobId, blob };
    }
    
    // Try base64 decode
    const textDecoder = new TextDecoder('utf-8');
    const responseText = textDecoder.decode(arrayBuffer);
    const cleanBase64 = responseText.replace(/\s/g, '');
    
    try {
      const binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      if (bytes.length > 1 && bytes[0] === 0x50 && bytes[1] === 0x4B) {
        const blob = new Blob([bytes.buffer], { 
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
        return { message: 'Resume generated (cached)', status: 'completed', jobId, blob };
      }
    } catch (decodeError) {
      logger.error('Base64 decode error', {
        error: decodeError instanceof Error ? decodeError.message : String(decodeError),
        jobId,
        templateId: templateId || 'default',
        responseLength: view.length,
      });
      Sentry.captureException(decodeError, {
        tags: { 
          component: 'api', 
          function: 'generateResume',
          errorType: 'decode',
        },
        extra: {
          jobId,
          templateId: templateId || 'default',
          responseLength: view.length,
        },
      });
    }
    
    const formatError = new Error('Response does not appear to be a valid DOCX file. Received ' + view.length + ' bytes.');
    Sentry.captureException(formatError, {
      tags: { 
        component: 'api', 
        function: 'generateResume',
        errorType: 'invalid_format',
      },
      extra: {
        jobId,
        templateId: templateId || 'default',
        responseLength: view.length,
      },
    });
    throw formatError;
  } else {
    // Return as JSON (queued status)
    return response.json();
  }
};

/**
 * Resume generation status for a single job
 */
export type ResumeStatus = {
  status: string;
  jobId: string | null;
  templateId: string | null;
  error: string | null;
  statusUpdatedAt: string | null;
  s3Key: string | null;
  downloadUrl: string | null;
  reportS3Key: string | null;
  reportUrl: string | null;
};

/**
 * Get resume generation status for all active jobs
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Map of jobId to status object, or single status object if jobId query param was provided (backward compatible)
 */
export const getResumeGenerationStatus = async (
  getAccessTokenSilently: GetAccessTokenSilently,
  jobId?: string
): Promise<{ statuses: Record<string, ResumeStatus> } | ResumeStatus> => {
  const url = jobId 
    ? `/users/me/generate-resume/status?jobId=${encodeURIComponent(jobId)}`
    : '/users/me/generate-resume/status';
  return apiRequest(url, { method: 'GET' }, getAccessTokenSilently);
};

/**
 * Get optimization report for the generated resume
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Optimization report object with original, optimized, and reason for each section
 */
export const getResumeOptimizationReport = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<any> => {
  return apiRequest('/users/me/generate-resume/report', { method: 'GET' }, getAccessTokenSilently);
};

/**
 * Create a Stripe Checkout session for purchasing credits
 * @param creditPackage - Number of credits to purchase (1, 5, or 10)
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Checkout session with sessionId and url
 */
export const createCheckoutSession = async (
  creditPackage: 1 | 5 | 10,
  getAccessTokenSilently: GetAccessTokenSilently
): Promise<{ sessionId: string; url: string }> => {
  return apiRequest('/users/me/checkout-session', {
    method: 'POST',
    body: JSON.stringify({ creditPackage })
  }, getAccessTokenSilently);
};

/**
 * Find jobs based on user profile using DeepSeek and Google Custom Search
 * @param getAccessTokenSilently - Auth0 function to get token
 * @returns Object with keywords, jobs array, and total count
 */
export const findJobs = async (getAccessTokenSilently: GetAccessTokenSilently): Promise<{
  keywords: string;
  jobs: Array<{
    title: string;
    company: string;
    location: string;
    url: string;
    snippet: string;
    source: string;
  }>;
  total: number;
}> => {
  return apiRequest('/users/me/find-jobs', {
    method: 'POST'
  }, getAccessTokenSilently);
};

